import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import cx from 'classnames'
import { isEqual } from 'lodash'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import NavigateNext from '@mui/icons-material/NavigateNext'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { bindActionCreators } from 'redux'
import withRouter from '../Router/WithRouter'
import { UsageInfoCard } from './UsageInfoCard'
import { LibrarianStatusCard } from './LibrarianStatusCard'
import {
  getMetadataContract,
  getSaveBulkMetadataContract,
  bulkEditErrorMessageBuilder,
  getMetadataContractNew,
} from '../../helpers/MetadataHelper'
import {
  updateBulkMetadata,
  fetchCatalogData,
  isMetadataFormEdited,
} from '../../store/metadata/actionCreator'
import {
  selectDownloadAssets,
  selectRightSideNavComponent,
  selectBSearchSelAssets,
} from '../../store/search/selector'
import { selectBoardDownloadAssets } from '../../store/boards/selector'
import {
  selectIsAdGroupSuperAdmin,
  selectIsLibrarian,
  selectUserEmail,
  selectUserId,
} from '../../store/auth/selector'
import {
  ASSET_REVIEW_PENDING,
  METADATA_CATALOG_TYPES,
} from '../../constants/metadata'
import { dispatchNotification } from '../../store/notification/actionCreator'
import { selectProjectSelectedAssets } from '../../store/projects/selector'
import { trackBulkUpdateMetadata } from '../../analytics/betaSearch'
import styles from './styles'
import ClassificationCard from './ClassificationCard'
import AssetDescriptors from './AssetDescriptors'
import ProductInfoCard from './ProductInfoCard'
import FileDetailsCard from './FileDetailsCard'
import {
  getProjectAssetsWithFilters,
  setSelectedAssets,
  setRefresh,
} from '../../store/projects/actionCreator'
import { Search } from '../../helpers/UtilityHelper'

const initialMetadataContract = getMetadataContract({})

export const BulkEditBar = (props) => {
  const {
    classes = {},
    consumer = '',
    selectedAssets: selectedAssetsProp = {},
    storedCatalog = {},
    fetchCatalogData: fetchCatalogDataProp = () => { },
    updateBulkMetadata: updateBulkMetadataProp = () => { },
    isMetadataFormEdited: isMetadataFormEditedProp = () => { },
    isMetadataEditEnabled,
    saveAlertActiontype = '',
    rightSideNavComponent = '',
    setSaveAlertAction = () => { },
    toggleRightSidenav = () => { },
    dispatchNotification: dispatchNotificationProp = () => { },
    lanId = '',
    email = '',
    selectedBoardAssets: selectedBoardAssetsProp = {},
    router = {},
    selectedProjectAssets = {},
    betaSearchSelectedAssets = [],
    selectedAssetGoldMetadata = {},
    setRefresh: setRefreshProp,
    setSelectedAssets: setSelectedAssetsProp,
    getProjectAssetsWithFilters: getProjectAssetsWithFiltersProp,
    bulkEditAssetIds = [],
    isGoldStandardLoading = false,
    pagination = {},
    filters = {},
  } = props

  const [selectedAssets, setSelectedAssetsState] = useState(selectedAssetsProp)
  const [selectedBoardAssets, setSelectedBoardAssets] = useState(selectedBoardAssetsProp)
  const [metadataState, setMetadataState] = useState(initialMetadataContract)
  const [metadataParentTimestamp] = useState(Date.now())
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [librarianReviewStatus, setLibrarianReviewStatus] = useState(ASSET_REVIEW_PENDING)
  const [newGoldStandards] = useState(true)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [classificationHasChanges, setClassificationHasChanges] = useState(false)

  // Refs to hold latest state for callbacks
  const stateRef = useRef()
  stateRef.current = {
    ...metadataState,
    selectedAssets,
    selectedBoardAssets,
    metadataParentTimestamp,
    unsavedChanges,
    isSaved,
    librarianReviewStatus,
    newGoldStandards,
    confirmDialogOpen,
    classificationHasChanges,
  }

  // Fetch catalog data on mount
  useEffect(() => {
    fetchCatalogDataProp(METADATA_CATALOG_TYPES)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync selectedAssets prop
  const prevSelectedAssetsRef = useRef(selectedAssetsProp)
  useEffect(() => {
    if (!isEqual(prevSelectedAssetsRef.current, selectedAssetsProp)) {
      setSelectedAssetsState(selectedAssetsProp)
    }
    prevSelectedAssetsRef.current = selectedAssetsProp
  }, [selectedAssetsProp])

  // Sync selectedBoardAssets prop
  const prevSelectedBoardAssetsRef = useRef(selectedBoardAssetsProp)
  useEffect(() => {
    if (!isEqual(prevSelectedBoardAssetsRef.current, selectedBoardAssetsProp)) {
      setSelectedBoardAssets(selectedBoardAssetsProp)
    }
    prevSelectedBoardAssetsRef.current = selectedBoardAssetsProp
  }, [selectedBoardAssetsProp])

  // Handle saveAlertActiontype trigger
  const prevSaveAlertRef = useRef(saveAlertActiontype)
  useEffect(() => {
    if (
      !isEqual(prevSaveAlertRef.current, saveAlertActiontype) &&
      saveAlertActiontype === 'save_' + rightSideNavComponent
    ) {
      saveMetadata()
    }
    prevSaveAlertRef.current = saveAlertActiontype
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveAlertActiontype, rightSideNavComponent])

  const checkEditPermissions = useCallback(() => {
    const canEdit = true // TODO: need to add logic here to allow edit
    return { canEdit }
  }, [])

  const updateContainerState = useCallback((updatedMetadata = {}) => {
    setMetadataState((prev) => ({
      ...Object.assign({}, prev, updatedMetadata),
    }))
    setUnsavedChanges(true)
    isMetadataFormEditedProp(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMetadataFormEditedProp])

  const saveBulkMetadataCallback = useCallback(
    (response, responseType, searchView, payload) => {
      const currentState = stateRef.current

      if (responseType === 'success') {
        if (searchView === Search) {
          trackBulkUpdateMetadata({
            authData: { lanId, email },
            bulkMetadataPayload: payload,
          })
        }
        setIsSaved(true)
        setUnsavedChanges(false)
        setSelectedAssetsState({})
        isMetadataFormEditedProp(false)
        setTimeout(() => {
          toggleRightSidenav(rightSideNavComponent, false)
        }, 1000)

        if (searchView === 'projects') {
          const projectId = response[0]?.metadata?.project_info?.project_uuid
          setSaveAlertAction && setSaveAlertAction('')
          getProjectAssetsWithFiltersProp(projectId, pagination, filters)
        }
      } else {
        const assetErrorMessages =
          searchView === 'boards'
            ? bulkEditErrorMessageBuilder(response, currentState.selectedBoardAssets)
            : bulkEditErrorMessageBuilder(response, currentState.selectedAssets)

        if (assetErrorMessages) {
          dispatchNotificationProp(true, assetErrorMessages, 'error', 50000)
        }
      }
      setRefreshProp(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      lanId,
      email,
      rightSideNavComponent,
      isMetadataFormEditedProp,
      toggleRightSidenav,
      setSaveAlertAction,
      getProjectAssetsWithFiltersProp,
      pagination,
      filters,
      dispatchNotificationProp,
      setRefreshProp,
    ],
  )

  const saveMetadata = useCallback(() => {
    const { location = {} } = router
    const { pathname = '' } = location
    const currentState = stateRef.current
    const updatedState = Object.assign({}, currentState)
    let view = Search
    const boardView = pathname.includes('/boards')
    const projectsView = pathname.includes('/project')
    const betaSearchView = pathname.includes(Search)
    if (boardView) {
      updatedState.selectedAssets = updatedState.selectedBoardAssets
      view = 'boards'
    } else if (projectsView) {
      updatedState.selectedAssets = selectedProjectAssets
      view = 'projects'
    } else if (betaSearchView) {
      updatedState.selectedAssets = betaSearchSelectedAssets
      view = Search
    }
    let payload
    if (bulkEditAssetIds.length === 1) {
      payload = getMetadataContractNew(selectedAssetGoldMetadata, updatedState)
    } else {
      payload = getSaveBulkMetadataContract(updatedState, 'new')
    }

    updateBulkMetadataProp(
      payload,
      lanId,
      saveBulkMetadataCallback,
      consumer,
      view,
    )
    setRefreshProp(true)
    setSelectedAssetsProp({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router,
    selectedProjectAssets,
    betaSearchSelectedAssets,
    selectedAssetGoldMetadata,
    updateBulkMetadataProp,
    lanId,
    saveBulkMetadataCallback,
    consumer,
    setRefreshProp,
    setSelectedAssetsProp,
  ])

  const handleCallBack = useCallback(() => {}, [])

  // Destructure metadata state for render
  const {
    usageInformation = [],
    usageInstructions = {},
    assetDetails = {},
    projectDetails = {},
    productInformation = [],
    storeInformation = [],
    assetDescriptions = {},
    assetClassifications = {},
  } = metadataState

  const libMetadata = {
    assetDetails,
    projectDetails,
    productInformation,
    storeInformation,
    assetDescriptions,
    assetClassifications,
  }

  const editPermissions = checkEditPermissions()
  const { location = {} } = router
  const { pathname = '' } = location
  const projectsView = pathname.includes('/project')
  const isSingleAssetEdit = bulkEditAssetIds.length === 1

  return (
    <div className={classes.metadataWrap}>
      {isGoldStandardLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading metadata...
          </Typography>
        </Box>
      ) : (
      <div className={classes.metadataScrollContent}>
        <div className={classes.goldMetaData}>
          {/* 1. Classification — only classification logic */}
          <ClassificationCard
            updateParentState={updateContainerState}
            selectedAssetGoldMetadata={selectedAssetGoldMetadata}
            banner={
              isSingleAssetEdit &&
              selectedAssetGoldMetadata?.metadata?.asset_classification &&
              Object.keys(
                selectedAssetGoldMetadata.metadata.asset_classification,
              ) === null
            }
            bulkEditAssetIds={bulkEditAssetIds}
            lanId={lanId}
            consume={consumer}
            view="project"
            handleCallBackResponse={handleCallBack}
            onClassificationChange={(hasChanges) => setClassificationHasChanges(hasChanges)}
            onClassificationSubmit={() => setClassificationHasChanges(false)}
          />

          <div style={{
            opacity: classificationHasChanges && bulkEditAssetIds.length === 1 ? 0.5 : 1,
            pointerEvents: classificationHasChanges && bulkEditAssetIds.length === 1 ? 'none' : 'auto'
          }}>
            {classificationHasChanges && bulkEditAssetIds.length === 1 && (
              <Alert severity="info" sx={{ marginBottom: '10px' }}>
                Please update classification first before editing metadata.
              </Alert>
            )}

            {/* 2. Product Section — TCIN / DPCI */}
            <div style={{ marginTop: '10px' }}>
              <ProductInfoCard
                updateParentState={updateContainerState}
                selectedAssetGoldMetadata={selectedAssetGoldMetadata}
                isBulkEdit={bulkEditAssetIds.length > 1}
              />
            </div>

            {/* 3. Usage Rights */}
            <div style={{ marginTop: '10px' }}>
              <UsageInfoCard
                updateParentState={updateContainerState}
                selectedAssetGoldMetadata={selectedAssetGoldMetadata}
                isBulkEdit={bulkEditAssetIds.length > 1}
              />
            </div>

            {/* 4. Descriptors — Brand, Is UGC, User Keywords */}
            <div style={{ marginTop: '10px' }}>
              <AssetDescriptors
                updateParentState={updateContainerState}
                selectedAssetGoldMetadata={selectedAssetGoldMetadata}
                storedCatalog={storedCatalog}
              />
            </div>

            {/* 5. File Details — Filename, Creator, Source, Model name/Agency */}
            <div style={{ marginTop: '10px' }}>
              <FileDetailsCard
                updateParentState={updateContainerState}
                selectedAssetGoldMetadata={selectedAssetGoldMetadata}
                usageInstructions={usageInstructions}
              />
            </div>

            {/* 6. Librarian Status — only for admins/librarians */}
            {isMetadataEditEnabled && (
              <div style={{ marginTop: '10px' }}>
                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="librarian-status-content"
                    id="librarian-status-header"
                  >
                    <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
                      Librarian Review Status
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <LibrarianStatusCard
                      editMode={true}
                      classes={classes}
                      librarianReviewStatus={librarianReviewStatus}
                      onLibrarianReviewStatusChange={(e) => {
                        setLibrarianReviewStatus(e.target.value)
                        setUnsavedChanges(true)
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      {/* Footer */}
      <div className={classes.metadataFooter}>
        <Button
          variant="outlined"
          endIcon={<NavigateNext />}
          sx={{
            color: '#2196F3',
            height: '42px',
            border: '1px solid #2196F3',
            width: '130px',
          }}
          onClick={() =>
            toggleRightSidenav(rightSideNavComponent, false)
          }
        >
          CANCEL
        </Button>
        <Button
          data-cy="bulkEditSave"
          className={cx(classes.actionButtonStyle)}
          variant="contained"
          id="bulkEditSaveButton"
          onClick={() => {
            saveMetadata()
          }}
          disabled={!unsavedChanges || (classificationHasChanges && bulkEditAssetIds.length === 1)}
          endIcon={<NavigateNext />}
        >
          UPDATE METADATA
        </Button>
      </div>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        style={{ zIndex: 1500 }}
      >
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          These changes cannot be undone. Do you want to proceed?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            color="primary"
            data-cy="save-no"
          >
            No
          </Button>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false)
              saveMetadata()
            }}
            color="primary"
            data-cy="save-yes"
            variant="contained"
            style={{ backgroundColor: "#3767c8" }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

BulkEditBar.propTypes = {
  classes: PropTypes.object,
  consumer: PropTypes.string,
  selectedAssets: PropTypes.object,
  storedCatalog: PropTypes.object,
  fetchCatalogData: PropTypes.func,
  updateBulkMetadata: PropTypes.func,
  isMetadataFormEdited: PropTypes.func,
  isMetadataEditEnabled: PropTypes.bool,
  saveAlertActiontype: PropTypes.string,
  rightSideNavComponent: PropTypes.string,
  setSaveAlertAction: PropTypes.func,
  toggleRightSidenav: PropTypes.func,
  dispatchNotification: PropTypes.func,
  lanId: PropTypes.string,
  selectedBoardAssets: PropTypes.object,
  location: PropTypes.object,
  getProjectAssetsWithFilters: PropTypes.func,
}

const mapStateToProps = (state = {}) => {
  const { metadata = {}, projects = {} } = state
  const { assetCatalog = {}, selectedAssetGoldMetadata = {}, bulkEditAssetIds = [], isGoldStandardLoading = false } = metadata
  const { pagination = {}, filters = {} } = projects

  return {
    storedCatalog: assetCatalog,
    bulkEditAssetIds,
    selectedAssets: selectDownloadAssets()(state),
    selectedBoardAssets: selectBoardDownloadAssets()(state),
    betaSearchSelectedAssets: selectBSearchSelAssets()(state),
    selectedProjectAssets: selectProjectSelectedAssets()(state),
    isMetadataEditEnabled:
      selectIsAdGroupSuperAdmin()(state) || selectIsLibrarian()(state),
    rightSideNavComponent: selectRightSideNavComponent()(state),
    lanId: selectUserId()(state),
    email: selectUserEmail()(state),
    selectedAssetGoldMetadata,
    isGoldStandardLoading,
    pagination,
    filters,
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchCatalogData,
      updateBulkMetadata,
      isMetadataFormEdited,
      dispatchNotification,
      getProjectAssetsWithFilters,
      setSelectedAssets,
      setRefresh,
    },
    dispatch,
  )

const MyComponent = (props) => {
  const classes = styles()
  return <BulkEditBar {...props} classes={classes} />
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(MyComponent))
